import React, {CSSProperties, useMemo, useState} from 'react';
import cx from 'classnames';
import {Icon, MissingImageIcon, Shimmer} from '@taopix/taopix-design-system';
import {Asset} from '../MediaGallery';
import {calcRotation, flipSize, getScaledSize} from '../../../Utils/Asset';
import {ThumbnailLayout} from './MediaGalleryThumbnailFactory';
import useImageLoader from '../../../Hooks/Assets/useImageLoader';

export type ThumbnailImageProps = {
  image: Asset;
  expired?: boolean;
  disabled?: boolean;
  openDetailsDialog?: Function;
  onDragStart?: (event: React.DragEvent<HTMLElement>, asset: Asset) => void;
  onDragEnd?: (event: React.DragEvent<HTMLElement>, asset: Asset) => void;
  onTouchStart?: (event: React.TouchEvent<HTMLElement>) => void;
  onTouchEnd?: (event: React.TouchEvent<HTMLElement>) => void;
  setLoaded: Function;
  classNames?: string;
  imageCSS?: CSSProperties;
  selectable?: boolean;
  thumbnailLayout?: ThumbnailLayout;
};

const ThumbnailImage = ({
  image,
  expired,
  disabled,
  onDragStart,
  onDragEnd,
  onTouchStart,
  onTouchEnd,
  setLoaded,
  classNames,
  imageCSS = {},
  selectable = true,
  thumbnailLayout = 'full-frame-tiles',
}: ThumbnailImageProps) => {
  const src = useImageLoader(image, image.assetType, true);
  const [imageLoaded, setImageLoaded] = useState(false);

  const onLoad = () => {
    // Set this component's state so it can hide the shimmer and display the image
    setImageLoaded(true);
    // Call the setLoaded function to tell the thumnail component it can now display the checkbox etc
    setLoaded(true);
  };

  const onError = () => {
    // display the placeholder on error /* TODO - add a small error icon at the top corner of the image? */
    // setImageLoaded(false);
    // setLoaded(false);
  };

  const rotation = useMemo(() => {
    return calcRotation(image);
  }, [image]);

  const sizing = flipSize(rotation, getScaledSize(image.width, image.height, 1000));

  const placeholderSize = {
    width: ['details-list', 'square-tiles'].includes(thumbnailLayout as ThumbnailLayout)
      ? 100
      : sizing.width,
    height: ['details-list', 'square-tiles'].includes(thumbnailLayout as ThumbnailLayout)
      ? 100
      : sizing.height,
  };

  const placeholderClassNames = cx(
    selectable && 'z-[-1]' // Position the placeholder canvas behind the checkbox border when it is present
  );

  const imageClasses = useMemo(() => {
    return [
      'absolute', // The image is absolutely positioned to sit above the placeholder (the placeholder is always the correct orientation but the image may be rotated)
      expired || disabled || !selectable ? '' : 'z-[-1]', // The z-index is to ensure that the absolutely-positioned image doesn't cover the outline of the containing element (e.g. checkbox)
      expired || disabled
        ? 'pointer-events-none opacity-40'
        : !selectable
        ? 'pointer-events-none'
        : 'cursor-pointer',
      !imageLoaded ? 'hidden' : '',
    ];
  }, [expired, disabled, imageLoaded]);

  const classList = useMemo(() => {
    return cx([classNames, imageClasses]);
  }, [imageClasses, classNames]);

  return (
    <>
      {
        // The placeholder is an invisible canvas that sits behind the image and is the correct proportions/orientation to ensure the container is the correct shape
        <canvas
          {...placeholderSize}
          style={{width: '100%', height: '100%'}}
          className={placeholderClassNames}
        />
      }

      {image?.status !== 8 && !imageLoaded && (
        // Display a loading shimmer
        <Shimmer {...placeholderSize} className="absolute top-0 left-0" />
      )}

      {image?.status === 8 || src === 'DENIED' ? (
        <Icon
          icon={<MissingImageIcon />}
          className="absolute m-auto top-0 left-0 bottom-0 right-0"
        />
      ) : (
        <img
          alt={thumbnailLayout}
          style={!imageLoaded ? {display: 'none'} : imageCSS}
          src={src}
          className={classList}
          draggable={undefined !== (onDragStart || onTouchStart)}
          onDragStart={onDragStart && (e => onDragStart(e, image))}
          onDragEnd={onDragEnd && (e => onDragEnd(e, image))}
          onTouchStart={onTouchStart && (e => onTouchStart(e))}
          onTouchEnd={onTouchEnd && (e => onTouchEnd(e))}
          onLoad={onLoad}
          onError={onError}
        />
      )}
    </>
  );
};

export default ThumbnailImage;
